import * as Yup from 'yup';

import { ORGANIZATION_MEMBERSHIP_CODE_TOTAL_DIGITS } from '@/components/ui/formats/organizationMembershipCode';
import {
  HIRAGANA_REGEXP,
  NAME_KANA_MAX_LENGTH,
  NAME_MAX_LENGTH,
  ORGANIZATION_MEMBERSHIP_CODE_REGEXP,
  PASSWORD_MAX_LENGTH,
  PASSWORD_MIN_LENGTH,
  PASSWORD_REGEXP,
  ZENKAKU_REGEXP,
} from '@/interfaces/registration';
import { HANKAKU_REGEXP, SAPURI_EMAIL_REGEXP } from '@/store/constants';
import { extractDisallowedChars } from '@/utils/disallowedCharsChecker';

/**
 * かな（ひらがな）のバリデーションスキーマを生成
 * @param fieldLabel - フィールドのラベル（例: '姓（かな）', '名（かな）'）
 */
const createKanaValidation = (fieldLabel: string) =>
  Yup.string()
    .required(`${fieldLabel}は必須です`)
    .matches(HIRAGANA_REGEXP, {
      message: `${fieldLabel}はひらがなで入力してください`,
      excludeEmptyString: true,
    })
    .max(
      NAME_KANA_MAX_LENGTH,
      `${fieldLabel}は${NAME_KANA_MAX_LENGTH}文字以内で入力してください`,
    );

export const validationSchema = Yup.object().shape({
  password: Yup.string()
    .required('パスワードは必須です')
    .test(
      'min',
      `パスワードは${PASSWORD_MIN_LENGTH}文字以上で入力してください`,
      (value) =>
        value ? value.length <= 0 || value.length >= PASSWORD_MIN_LENGTH : true,
    )
    .max(
      PASSWORD_MAX_LENGTH,
      `パスワードは${PASSWORD_MAX_LENGTH}文字以内で入力してください`,
    )
    .matches(PASSWORD_REGEXP, {
      message:
        'パスワードは半角アルファベットと半角数字をそれぞれ1文字以上で入力してください',
      excludeEmptyString: true,
    })
    .test(
      'not-same-as-email',
      'メールアドレスとパスワードに同じ文字列は使用できません',
      function (value) {
        const { email } = this.parent;
        // emailとpasswordの両方が空でない場合のみチェック
        if (
          !value ||
          !email ||
          (typeof value === 'string' && value.trim() === '') ||
          (typeof email === 'string' && email.trim() === '')
        ) {
          return true;
        }
        return value !== email;
      },
    ),
  password_confirmation: Yup.string()
    .required('パスワード（確認用）は必須です')
    .oneOf([Yup.ref('password')], 'パスワードが一致しません'),
  last_name: Yup.string()
    .trim()
    .required('姓は必須です')
    // バリデーション順序:
    // 1. 全角チェック（半角文字を先に弾く）
    // 2. 機種依存文字チェック（JIS X 0208範囲外の文字を検出）
    .matches(ZENKAKU_REGEXP, {
      message: '姓は全角文字で入力してください',
      excludeEmptyString: true,
    })
    .test(
      'no-disallowed-chars',
      '姓に機種依存文字は使用できません',
      function (value) {
        if (!value) return true;
        const disallowedChars = extractDisallowedChars(value);
        if (disallowedChars.length === 0) return true;
        return this.createError({
          message: `姓に${disallowedChars.join('、')}は使用できません`,
        });
      },
    )
    .max(NAME_MAX_LENGTH, `姓は${NAME_MAX_LENGTH}文字以内で入力してください`),
  first_name: Yup.string()
    .trim()
    .required('名は必須です')
    // バリデーション順序:
    // 1. 全角チェック（半角文字を先に弾く）
    // 2. 機種依存文字チェック（JIS X 0208範囲外の文字を検出）
    .matches(ZENKAKU_REGEXP, {
      message: '名は全角文字で入力してください',
      excludeEmptyString: true,
    })
    .test(
      'no-disallowed-chars',
      '名に機種依存文字は使用できません',
      function (value) {
        if (!value) return true;
        const disallowedChars = extractDisallowedChars(value);
        if (disallowedChars.length === 0) return true;
        return this.createError({
          message: `名に${disallowedChars.join('、')}は使用できません`,
        });
      },
    )
    .max(NAME_MAX_LENGTH, `名は${NAME_MAX_LENGTH}文字以内で入力してください`),
  last_name_kana: createKanaValidation('姓（かな）'),
  first_name_kana: createKanaValidation('名（かな）'),
  email: Yup.string()
    .required('メールアドレスは必須です')
    .test('email-format', 'メールアドレスの形式が不正です', (value) => {
      if (!value) return true;
      if (/\.$/.test(value)) return false;
      return SAPURI_EMAIL_REGEXP.test(value);
    })
    .matches(HANKAKU_REGEXP, {
      message: 'メールアドレスは半角英数字で入力してください',
      excludeEmptyString: true,
    }),
  code: Yup.string()
    .required('団体会員コードは必須です')
    .test(
      'code-invalid-message-1',
      '入力された団体会員コードは使用できません',
      (value) => {
        if (!value) return true;
        return ORGANIZATION_MEMBERSHIP_CODE_REGEXP.test(value);
      },
    )
    .test(
      'code-invalid-message-2',
      '内容に誤りがないかもう一度お確かめください',
      (value) => {
        if (!value) return true;
        return ORGANIZATION_MEMBERSHIP_CODE_REGEXP.test(value);
      },
    ),
});

// 入力途中はエラーを出したくないフォーム（登録画面 / マイページの分割入力）向けの「緩い」バリデーション。
// 空・空白は必須エラーにするが、規定桁数未満の数字は valid のままにして、入力途中に
// 「使用できません」が出るのを防ぐ。「ちょうど規定桁数か」は各フォームが送信ボタンの活性
// （isCodeReadyForOk）で別途判定する。
export const codeStrictValidation = Yup.string()
  .test('code-required', '団体会員コードは必須です', (value) => {
    const cleaned = value?.replace(/\s/g, '') ?? '';
    return cleaned.length > 0;
  })
  .test('code-invalid', '入力された団体会員コードは使用できません', (value) => {
    const cleaned = value?.replace(/\s/g, '') ?? '';
    if (cleaned.length === 0) return true;
    return (
      /^\d+$/.test(cleaned) &&
      cleaned.length <= ORGANIZATION_MEMBERSHIP_CODE_TOTAL_DIGITS
    );
  });

/**
 * 生徒氏名（かな）フィールドを含むバリデーションスキーマを作成
 */
export const createValidationSchema = () =>
  Yup.object().shape({
    ...validationSchema.fields,
    code: codeStrictValidation,
    student_last_name_kana: createKanaValidation('姓（かな）'),
    student_first_name_kana: createKanaValidation('名（かな）'),
  });
